import { useTranslation } from "react-i18next"

import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { ToastType } from "shared/types/Toast"
import { ProductInformation } from "shared/types/__generated__/graphql"

function createClipboardText({
  productInformation,
  titleSuffix
}: {
  productInformation?: ProductInformation | null
  titleSuffix?: string
}) {
  if (!productInformation) {
    return ""
  }

  const title = `${
    productInformation.title || productInformation.variantKey || ""
  }${titleSuffix ? ` ${titleSuffix}` : ""}`

  const variantKeyLine = !title.includes(productInformation.variantKey || "")
    ? `${productInformation.variantKey}\n`
    : ""

  return (
    `${title}\n` +
    `${variantKeyLine}` +
    "\n" +
    `${productInformation.generalDescription?.label}\n` +
    `${productInformation.generalDescription?.value}\n\n` +
    `${productInformation.variantDescription?.label}\n` +
    `${productInformation.variantDescription?.value}`
  )
}

export function useProductInformationClipboardShare(
  defaultProductInformation?: ProductInformation | null
) {
  const { t } = useTranslation()

  const handleShare = useEvent(
    async (productInformation?: ProductInformation | null) => {
      const resolvedProductInformation =
        productInformation ?? defaultProductInformation
      if (!resolvedProductInformation) return
      const clipboardText = `${createClipboardText({
        productInformation: resolvedProductInformation
      })}\n${
        resolvedProductInformation.addons?.reduce(
          (acc, addon) =>
            `${acc}\n${createClipboardText({
              productInformation: addon,
              titleSuffix: `(${t("Product.Configuration.Addon.Addon")})`
            })}`,
          ""
        ) || ""
      }`

      navigator.clipboard.writeText(clipboardText).then(() => {
        raiseToast({
          type: ToastType.SUCCESS,
          message: t("General.CopiedToClipboard")
        })
      })
    }
  )

  return handleShare
}
